<script setup>
import { computed, ref } from "vue";
import { VueFinalModal } from "vue-final-modal";
import { useVuelidate } from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import { useStore } from "vuex";

import i18n from "@/i18n";

const store = useStore();

const emailField = ref("");

const isCloseVisible = computed(
  () => emailField.value,
  () => emailField.value > 3
);
function resetField() {
  emailField.value = "";
}
const emailNotEmpty = computed(() =>
  i18n.global.t("login.errors.emailNotEmpty")
);
const emailNotValid = computed(() =>
  i18n.global.t("login.errors.emailNotValid")
);
const errorLoginMessage = computed(() =>
  i18n.global.t(store.getters.errorLogin || "_")
);
const isFirebaseLoginError = () => {
  return store.getters.errorLogin == null;
};
const isForgotSuccessReset = computed(() => {
  return store.getters.messageForgotSuccess == null;
});
const rules = computed(() => {
  return {
    emailField: {
      required: helpers.withMessage(emailNotEmpty, required),
      email: helpers.withMessage(emailNotValid, email),
      isFirebaseLoginError: helpers.withMessage(
        errorLoginMessage,
        isFirebaseLoginError
      ),
    },
  };
});

const v$ = useVuelidate(rules, { emailField });
const resetPassword = async () => {
  try {
    v$.value.$reset();
    store.commit("SET_ERROR_LOGIN", null);
    const resultValidate = await v$.value.$validate();
    if (resultValidate) {
      store.dispatch("reset_password", { email: emailField.value });
    } else {
      console.log("error validate", resultValidate);
    }
  } catch (error) {
    console.log("error", error);
  }
};

function goBack() {
  emit("confirm");
}
const forgotSuccessReset = computed(() =>
  i18n.global.t("login.forgotQuery.messageSuccessReset")
);
const okReset = () => {
  emit("confirm");
  store.commit("CLEAR_MESSAGE_FOR_SUCCESS_RESET");
};

// eslint-disable-next-line no-undef
defineProps({});
// eslint-disable-next-line no-undef
const emit = defineEmits({
  confirm: null,
});
</script>

<template>
  <vue-final-modal
    class="forgot-password__main"
    overlay-class="forgot-password__overlay"
    content-class="forgot-password__content"
  >
    <div
      v-if="isForgotSuccessReset"
      class=""
    >
      <div class="forgot-password__top">
        <v-icon
          name="qbeek-forgot-password"
          width="124"
          height="100"
          stroke="none"
        />
        <div class="forgot-password__title">
          Забыли пароль?
        </div>
        <div class="forgot-password__subtitle">
          Введите электронную почту, указанную при регистрации. Мы отправим вам
          ссылку для сброса пароля.
        </div>
      </div>
      <form @submit.prevent="resetPassword">
        <div
          :class="['forgot-password__email', { error: v$.emailField.$error }]"
        >
          <div class="wrapper-input-forget">
            <input
              v-model="emailField"
              class="forgot-password__input"
              name="email"
              placeholder="Введите вашу почту"
              type="email"
              autocomplete="username"
              @focus="v$.$reset()"
            >
            <v-icon
              v-if="isCloseVisible"
              class="forgot-password__clear"
              name="x1"
              width="16"
              height="16"
              @click="resetField"
            />
            <span
              v-if="v$.emailField.$error"
              class="reg-form__password-error"
            >
              {{ v$.emailField.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="forgot-password__buttons">
          <action-button
            class="flex-1"
            @click="goBack"
          >
            Отмена
          </action-button>
          <action-button
            class="flex-1"
            type="submit"
            action
          >
            Сбросить пароль
          </action-button>
        </div>
      </form>
    </div>
    <div
      v-else
      class="forgotSuccessReset"
    >
      {{ forgotSuccessReset }}
      <div class="buttons">
        <!-- <button class="btn" @click="okReset">OK</button> -->
        <action-button
          w100p
          class="flex1"
          action
          @click="okReset"
        >
          OK
        </action-button>
      </div>
    </div>
    <div class="forgot-password__close">
      <v-icon
        name="x"
        width="24"
        height="24"
        stroke="#0E0E22"
        @click="emit('confirm')"
      />
    </div>
  </vue-final-modal>
</template>
<style lang="scss">
.forgot-password {
  color: #0e0e22;
  &__buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }

  &__main {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &__overlay {
    background: rgba(14, 14, 34, 0.8) !important;
    backdrop-filter: blur(2px) !important;
  }
  &__content {
    width: 420px;
    position: relative !important;
    background: #ffffff;
    border-radius: 20px;
    padding: 32px 24px;
    position: relative;
    box-shadow: 0px 4px 40px 0px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 20px;
    &__icons {
      display: flex;
      gap: 12px;
      justify-content: center;

      &--text {
        color: #bcbbbb;
        font: 400 12px/130% "Inter", sans-serif;
        text-align: center;
        line-height: 52px;
      }
    }

    &__text-block {
      text-align: center;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 20px;
      align-items: center;
      justify-content: center;
      width: 320px;
    }
  }
  &__close {
    position: absolute !important;
    top: 8px !important;
    right: 8px !important;
    cursor: pointer;
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  &__title {
    font: normal 600 20px/110% Montserrat;
    text-align: center;
  }
  &__subtitle {
    font: normal 400 14px/130% Inter;
    color: #595a5b;
    text-align: center;
    margin-bottom: 20px;
  }
  &__email {
    position: relative !important;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    align-self: stretch;
    border-radius: 8px;
    width: 100%;

    overflow: hidden;
  }

  &__clear {
    position: absolute !important;
    top: 50%;
    right: 16px;
    transform: translateY(-50%) !important;
    /* top: 8px !important;
    right: 8px !important; */
    cursor: pointer;
  }

  &__input {
    padding: 0px 38px 0px 16px;
    height: 40px;
    border: none;
    outline: none;
    border-radius: 8px;
    color: #0e0e22;
    font: 400 14px/130% "Inter", sans-serif;
    border: 1px solid #a0a0a0;
    width: 100%;
  }
  &__password-error {
    color: #f73a53;
  }
}
.error {
  font-size: 12px;
  line-height: 130%;
  color: #f73a53;
}
.input-error {
  border: 1px solid #f73a53 !important;
  outline: none !important;
  color: #f73a53;
}
.wrapper-input-forget {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 20px;
}
.flex-1 {
  flex: 1;
}
.forgotSuccessReset {
  font-size: 16px;
  line-height: 130%;
  text-align: center;

  color: #9b9b9b;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
